import React, { useState } from 'react';
import { FiDownload, FiAlertTriangle, FiList } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { artifactoryService } from '../../services/artifactoryService';
import { useTheme } from '../../contexts/ThemeContext';

interface ExportButtonProps {
  sessionId: string;
  manifestId: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({ sessionId, manifestId }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [includeStale, setIncludeStale] = useState(false);
  const [includeGhost, setIncludeGhost] = useState(false);
  const [hydrationStatus, setHydrationStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportTimestamp, setExportTimestamp] = useState<Date | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setExportSuccess(false);

    try {
      // First check hydration status
      const status = await artifactoryService.getProjectHydrationStatus(manifestId);
      setHydrationStatus(status);

      // If project is fully hydrated or user has chosen to include stale/ghost nodes
      if (status.is_fully_hydrated || (includeStale && includeGhost)) {
        // Trigger download
        const url = `/api/artifactory/export/${manifestId}?include_stale=${includeStale}&include_ghost=${includeGhost}`;

        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${status.project_name || 'project'}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        setExportSuccess(true);
        setExportTimestamp(new Date());
        
        // Hide options after successful export
        setShowOptions(false);
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setExportSuccess(false);
        }, 5000);
      } else {
        // Show options if project is not fully hydrated
        setShowOptions(true);
      }
    } catch (error) {
      console.error('Failed to export project:', error);
      setError(error.message || 'Failed to export project');
    } finally {
      setIsExporting(false);
    }
  };

  const goToHydrationPlan = () => {
    navigate(`/artifactory/${sessionId}/hydration-plan/${manifestId}`);
  };

  return (
    <div className="relative">
      <button
        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
        onClick={handleExport}
        disabled={isExporting}
      >
        {isExporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
            Exporting...
          </>
        ) : (
          <>
            <FiDownload className="mr-2" />
            Export Project
          </>
        )}
      </button>

      {showOptions && hydrationStatus && (
        <div className={`absolute right-0 mt-2 w-96 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-md shadow-lg z-10 p-4`}>
          <h3 className="font-semibold mb-2 flex items-center">
            <FiAlertTriangle className="text-yellow-500 mr-2" />
            Project Not Fully Hydrated
          </h3>

          <div className="mb-4 text-sm">
            <p>This project has:</p>
            <ul className="list-disc pl-5 mt-1">
              <li>
                <span className="font-medium text-green-500">{hydrationStatus.fresh_nodes}</span> hydrated files 
                <span className="ml-1 text-xs text-gray-500">(ready for export)</span>
              </li>
              <li>
                <span className="font-medium text-orange-500">{hydrationStatus.stale_nodes}</span> stale files 
                <span className="ml-1 text-xs text-gray-500">(need regeneration)</span>
                <div className="text-xs ml-5 mt-1 text-gray-400">
                  Stale files have dependencies that changed and may need updating
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-500">{hydrationStatus.ghost_nodes}</span> ungenerated files 
                <span className="ml-1 text-xs text-gray-500">(not created yet)</span>
                <div className="text-xs ml-5 mt-1 text-gray-400">
                  Ghost files exist in the project structure but haven't been generated
                </div>
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <div className={`p-2 mb-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className="text-xs mb-2">
                You can export the project as-is with the following options:
              </p>
              
              <label className="flex items-center p-2 rounded hover:bg-opacity-50 hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={includeStale}
                  onChange={(e) => setIncludeStale(e.target.checked)}
                  className="mr-2"
                />
                <div>
                  <div className="flex items-center">
                    <span className="font-medium">Include stale files</span>
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {hydrationStatus.stale_nodes}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Stale files will include warning comments about needing regeneration
                  </p>
                </div>
              </label>

              <label className="flex items-center p-2 rounded hover:bg-opacity-50 hover:bg-gray-600 mt-1">
                <input
                  type="checkbox"
                  checked={includeGhost}
                  onChange={(e) => setIncludeGhost(e.target.checked)}
                  className="mr-2"
                />
                <div>
                  <div className="flex items-center">
                    <span className="font-medium">Include ghost files</span>
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-800'
                    }`}>
                      {hydrationStatus.ghost_nodes}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Ghost files will be exported as placeholder stubs with TODO comments
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              className={`px-3 py-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded`}
              onClick={() => setShowOptions(false)}
            >
              Cancel
            </button>

            <button
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
              onClick={handleExport}
              disabled={!includeStale || !includeGhost}
            >
              Export Anyway
            </button>
          </div>

          <div className="mt-3 text-center">
            <button
              onClick={goToHydrationPlan}
              className="text-blue-500 hover:underline flex items-center justify-center mx-auto"
            >
              <FiList className="mr-1" />
              View Hydration Plan
            </button>
          </div>
        </div>
      )}

      {exportSuccess && (
        <div className={`mt-2 text-sm px-3 py-2 rounded-md ${
          darkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-700'
        }`}>
          Export successful! {exportTimestamp && `(${exportTimestamp.toLocaleTimeString()})`}
        </div>
      )}
      
      {error && (
        <div className="mt-2 text-red-500 text-sm px-3 py-2 rounded-md bg-red-100 bg-opacity-20">
          {error}
        </div>
      )}
    </div>
  );
};
